<template>
    <div
        ref="nearbyLocations"
        class="search-geolink flow-column"
        :class="{ 'show-more': showMore }"
        v-if="currentList && currentList.length"
        comp="searchGeolink"
    >
         <div>
             <h2 class="f4 text-bold">Explore nearby locations</h2>
             <span class="text-gray">The prices shown are the median list prices.</span>
         </div>
        <ul>
            <li>
                <button :class="{ active: type === 'city' }" type="button" v-if="cities.length" @click="toggle('city')">
                    {{ molang('search.common.cities') }}
                </button>
            </li>
            <li>
                <button :class="{ active: type === 'zipcode' }" type="button" v-if="zipcodes.length" @click="toggle('zipcode')">
                    {{ molang('search.common.zipCodes') }}
                </button>
            </li>
            <li>
                <button :class="{ active: type === 'neighborhood' }" type="button" v-if="neighborhoods.length" @click="toggle('neighborhood')">
                    {{ molang('search.common.neighborhoods') }}
                </button>
            </li>
            <li>
                <button :class="{ active: type === 'county' }" type="button" v-if="counties.length" @click="toggle('county')">
                    {{ molang('search.common.counties') }}
                </button>
            </li>
        </ul>
        <div class="search-linklist" :class="{ show: type === 'city' }" v-if="cities.length">
            <div v-for="city in cities" :key="city.name">
                <a :href="`${glb.appUrl}${city.url}`"
                    ><b>{{ city.name }}</b> {{ linkSuffix }}</a
                >
                <p v-if="city.price">{{ $filters.formatPrice(city.price) }}</p>
            </div>
        </div>
        <div class="search-linklist" :class="{ show: type === 'zipcode' }" v-if="zipcodes.length">
            <div v-for="zipcode in zipcodes" :key="zipcode.name">
                <a :href="`${glb.appUrl}${zipcode.url}`"
                    ><b>{{ zipcode.name }}</b> {{ linkSuffix }}</a
                >
                <p v-if="zipcode.price">{{ $filters.formatPrice(zipcode.price) }}</p>
            </div>
        </div>
        <div class="search-linklist" :class="{ show: type === 'neighborhood' }" v-if="neighborhoods.length">
            <div v-for="neighborhood in neighborhoods" :key="neighborhood.name">
                <a :href="`${glb.appUrl}${neighborhood.url}`"
                    ><b>{{ neighborhood.name }}</b> {{ linkSuffix }}</a
                >
                <p v-if="neighborhood.price">{{ $filters.formatPrice(neighborhood.price) }}</p>
            </div>
        </div>
        <div class="search-linklist" :class="{ show: type === 'county' }" v-if="counties.length">
            <div v-for="county in counties" :key="county.name">
                <a :href="`${glb.appUrl}${county.url}`"
                    ><b>{{ county.name }}</b> {{ linkSuffix }}</a
                >
                <p v-if="county.price">{{ $filters.formatPrice(county.price) }}</p>
            </div>
        </div>
        <button v-if="currentList && currentList.length > 5" type="button" @click="toggleShowMore">
            {{ showMore ? 'Show Less' : 'Show More' }} <i :class="showMore ? 'icon-angle-up' : 'icon-angle-down'"></i>
        </button>
    </div>
</template>
<script>
import { mapGetters } from 'vuex';

export default {
    name: 'searchGeoLink',
    data() {
        return {
            selectedType: '',
            showMore: false,
        };
    },
    methods: {
        toggle(type) {
            this.selectedType = type;
        },
        toggleShowMore() {
            this.showMore = !this.showMore;
        },
    },
    computed: {
        ...mapGetters('glb', ['glb']),
        ...mapGetters('msp', [
            'getGeoDisplayText',
            'getNearbyCities',
            'getNearbyZipcodes',
            'getNearbyNeighborhood',
            'getNearbyCounties',
        ]),
        linkSuffix() {
            const key = this.glb.rentals ? 'search.common.homesForRentIn' : 'search.common.homesForSaleIn';
            return (this.molang(key) || '').replace(/\s+in\s*$/i, '') || (this.glb.rentals ? 'homes for rent' : 'homes for sale');
        },
        type() {
            if (this.selectedType) {
                return this.selectedType;
            }
            if (this.getNearbyCities && this.getNearbyCities.length) {
                return 'city';
            }
            if (this.getNearbyZipcodes && this.getNearbyZipcodes.length) {
                return 'zipcode';
            }
            if (this.getNearbyNeighborhood && this.getNearbyNeighborhood.length) {
                return 'neighborhood';
            }
            if (this.getNearbyCounties && this.getNearbyCounties.length) {
                return 'county';
            }
            return null;
        },
        cities() {
            if (!this.getNearbyCities || !this.getNearbyCities.length) return [];
            return this.getNearbyCities
                .filter((city) => city.sitemapCityPropertyPageUrl)
                .slice(0, 10)
                .map((city) => ({
                    name: city.displayName,
                    price: city.medianListPrice,
                    url: city.sitemapCityPropertyPageUrl,
                }))
        },
        zipcodes() {
            if (!this.getNearbyZipcodes || !this.getNearbyZipcodes.length) return [];
            return this.getNearbyZipcodes
                .filter((zipcode) => zipcode.sitemapZipCodePageUrl)
                .slice(0, 10)
                .map((zipcode) => ({
                    name: `${zipcode.displayName}, ${zipcode.stateCode}`,
                    price: zipcode.medianListPrice,
                    url: zipcode.sitemapZipCodePageUrl,
                }));
        },
        neighborhoods() {
            if (!this.getNearbyNeighborhood || !this.getNearbyNeighborhood.length) return [];
            return this.getNearbyNeighborhood
                .filter((n) => n.sitemapNeighborhoodPageUrl || n.neighborhoodPath)
                .slice(0, 10)
                .map((neighborhood) => ({
                    name: neighborhood.displayName || neighborhood.neighborhood,
                    price: neighborhood.medianListPrice,
                    url: neighborhood.sitemapNeighborhoodPageUrl || neighborhood.neighborhoodPath,
                }));
        },
        counties() {
            if (!this.getNearbyCounties || !this.getNearbyCounties.length) return [];
            return this.getNearbyCounties
                .filter((county) => county.sitemapCountyPageUrl)
                .slice(0, 10)
                .map((county) => ({
                    name: county.displayName,
                    price: county.medianListPrice,
                    url: county.sitemapCountyPageUrl,
                }));
        },
        currentList() {
            if (this.type === 'city') return this.cities;
            if (this.type === 'zipcode') return this.zipcodes;
            if (this.type === 'neighborhood') return this.neighborhoods;
            if (this.type === 'county') return this.counties;
            return null;
        },
    },
};
</script>
<style lang="scss">
@use './search-geolink.scss' as *;
</style>
